import React, { useState } from "react";
import {
  Avatar, Box, Card, Divider, IconButton, Link, ListItemIcon, ListItemText,
  Menu, MenuItem, Stack, Typography,
} from "@mui/material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Row } from "../types";
import {
  AvailableCell, ClassCell, GradeSubjectCell, ModeLocationCell,
  ScheduleCell, StatusChip, UsageCell, stableAvatar,
} from "./rowHelpers";

// Mobile card variant — one card per row so users don't have to scroll a wide
// table sideways on a small screen.
type Props = {
  row: Row;
  onViewStaff: (staffId: number) => void;
  onViewClass: (classId: number) => void;
  onViewStudents: (classId: number) => void;
  onOpenSchedule: (classId: number) => void;
};

export default function MobileCard({
  row, onViewStaff, onViewClass, onViewStudents, onOpenSchedule,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const avatar = stableAvatar(row.staff_name || "?");

  return (
    <Card variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar sx={{ bgcolor: avatar.bg, color: avatar.color, width: 36, height: 36, fontSize: 13 }}>
          {avatar.initials}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Link component="button" underline="hover"
            onClick={() => row.staff_id && onViewStaff(row.staff_id)}
            disabled={!row.staff_id}
            sx={{ fontWeight: 600, color: "#1b3a6b" }}>
            {row.staff_name || "—"}
          </Link>
          {row.staff_code && (
            <Typography variant="caption" color="text.secondary" display="block">
              Staff ID {row.staff_code}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem disabled={!row.staff_id}
            onClick={() => { setMenuAnchor(null); row.staff_id && onViewStaff(row.staff_id); }}>
            <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Staff" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewClass(row.class_id); }}>
            <ListItemIcon><ClassOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Class" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewStudents(row.class_id); }}>
            <ListItemIcon><PeopleAltOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Students" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onOpenSchedule(row.class_id); }}>
            <ListItemIcon><EventOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Open Schedule" />
          </MenuItem>
        </Menu>
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={1}>
        <ClassCell row={row} />
        <GradeSubjectCell row={row} />
        <ScheduleCell row={row} />
        <ModeLocationCell row={row} />
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">
            Students <strong>{row.active_students}</strong>
            {row.capacity !== null && ` / ${row.capacity}`}
          </Typography>
          <AvailableCell row={row} />
          <Box flex={1} minWidth={120}><UsageCell row={row} /></Box>
        </Stack>
        <StatusChip row={row} />
      </Stack>
    </Card>
  );
}
